import { useCallback } from "react";
import { DrawerBody, DrawerFooter } from "@chakra-ui/react";
import { v4 as uuidv4 } from "uuid";
import { assertBrandId, assertPresence } from "utils/assertions";
import { addNewRecordsWithFieldSpec } from "utils/apollo/cacheHelpers";
import { useBrandMutation } from "utils/apollo/hooks/brand";
import { useCurrentBrandId } from "utils/currentUserHooks";
import useCreateValidSubmitHandler, {
  PerformUpdateHandler,
} from "components/Shared/Forms/useCreateValidSubmitHandler";
import { DocumentType, gql } from "__generated__/gql";
import { trackEvent } from "utils/segment";
import { updateCachedProductLineSkus } from "components/Products/queries";
import EditPanelContent, {
  CancelButton,
  SaveButton,
} from "components/Products/EditPanelContent";
import {
  UNARCHIVED_STATUSES,
  getArchivedSkuFilter,
} from "components/Products/utils";
import ProductLinePanelForm, {
  FORM_ID,
  processApolloError,
  ProductLineInputs,
} from "./Form";

const mutation = gql(/* GraphQL */ `
  mutation CreateProductLine(
    $productLineId: UUID!
    $productLineData: ProductLineInput!
    $productCategoryIds: [UUID!]!
  ) {
    createProductLine(input: { productLine: $productLineData }) {
      productLine {
        id
        name
      }
    }
    setProductCategoryDepartmentSubDepartmentsForProductLine(
      input: {
        productLineId: $productLineId
        productCategoryDepartmentSubDepartmentIds: $productCategoryIds
      }
    ) {
      productLine {
        id
        name
        ...ProductLineFields
        ...ProductLinesSkusTableFieldsFragment
      }
    }
  }
`);

export type CreateResult = DocumentType<typeof mutation>;

type CreatedProductLine = NonNullable<
  CreateResult["createProductLine"]
>["productLine"];

type Props = {
  onClose: () => void;
  onNewCancel?: () => void;
  onNewSuccess?: (data: CreateResult) => void;
};

export default function ProductLineCreate({
  onClose,
  onNewCancel,
  onNewSuccess,
}: Props) {
  const brandId = useCurrentBrandId();

  const [createProductLine, { loading: isCreating }] = useBrandMutation(
    mutation,
    {
      onCompleted: (newProductLine) => {
        trackEvent("Product Line Created");
        if (onNewSuccess) onNewSuccess(newProductLine);
      },
      update: (cache, result) => {
        assertBrandId(brandId);

        const newProductLine = result.data?.createProductLine?.productLine;
        assertPresence(newProductLine);

        updateCachedProductLineSkus(
          cache,
          newProductLine,
          UNARCHIVED_STATUSES,
          (existing) => existing || [],
        );

        addNewRecordsWithFieldSpec({
          cache,
          baseFieldName: "productLines",
          newObjs: [newProductLine],
          getArgs: (existing) =>
            UNARCHIVED_STATUSES.map((status) => ({
              ...existing,
              condition: { brandId },
              filter: getArchivedSkuFilter(status),
            })),
        });
      },
    },
  );

  const [handleValidSubmit] = useCreateValidSubmitHandler(
    useCallback<PerformUpdateHandler<ProductLineInputs, CreatedProductLine>>(
      async (formData) => {
        const { productCategoryIds = [], ...productLineData } = formData;

        assertBrandId(brandId);
        const newProductLineId = uuidv4();
        const { data: createData } = await createProductLine({
          variables: {
            productLineId: newProductLineId,
            productLineData: {
              ...productLineData,
              id: newProductLineId,
              brandId,
            },
            productCategoryIds,
          },
        });
        const pl = createData?.createProductLine?.productLine;
        assertPresence(pl);
        return pl;
      },
      [brandId, createProductLine],
    ),
    {
      getSuccessToast: (result) => ({
        title: `Product line created successfully: ${result?.name}`,
      }),
      processApolloError,
    },
  );

  return (
    <EditPanelContent
      onClose={onClose}
      description="Add New Product Line"
      headingPlaceholder="New Product Line"
    >
      <DrawerBody>
        <ProductLinePanelForm onValidSubmit={handleValidSubmit} isCreateNew />
      </DrawerBody>

      <DrawerFooter gap={4}>
        {onNewCancel && <CancelButton onClick={onNewCancel} />}

        <SaveButton isLoading={isCreating} form={FORM_ID}>
          Create
        </SaveButton>
      </DrawerFooter>
    </EditPanelContent>
  );
}
